import { useState, useEffect } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Chip, FormControl, InputLabel, Select, MenuItem, Grid, Container, TablePagination } from '@mui/material';
import { httpRequest } from 'src/services/httpRequest';
import { PetType,LicenseStatus,PetStatus } from './pet.types';

export enum PetTypes{
    DOG = 'Dog',
    CAT = 'Cat',
    All = 'All'
}

export const PET_TYPES = [
    PetType.ALL,
    PetType.DOG,
    PetType.CAT,
];
export const PET_STATUSES = [
    PetStatus.ACTIVE,
    PetStatus.INACTIVE,
];

export const LICENSE_STATUSES = [
    LicenseStatus.ALL,
    LicenseStatus.ACTIVE,
    LicenseStatus.EXPIRED,
    LicenseStatus.EXPIRING_SOON,
];
const PetLicenseDetails = () => {
    //state for pet data
    const [pets, setPets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedPetType, setSelectedPetType] = useState<PetType>(PetType.ALL);

    //state for pagination & filtering
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [licenseStatusFilter, setLicenseStatusFilter] = useState<LicenseStatus>(LicenseStatus.ALL);

    // Define columns for the table
    const columns = [
        { field: 'PetName', headerName: 'Pet Name' },
        { field: 'PetType', headerName: 'Pet Type' },
        { field: 'OwnerName', headerName: 'Owner Name' },
        { field: 'PhoneNumber', headerName: 'Phone Number' },
        { field: 'ExpiryDate', headerName: 'Expiry Date' },
        { field: 'PetStatus', headerName: 'Pet Status' },
        { field: 'LicenseStatus', headerName: 'License Status' },
        { field: 'Actions', headerName: 'Actions' },
    ];
    // Fetch data with filters and pagination
    const fetchPets = async () => {
        setLoading(true);
        try {
            const response = await httpRequest(
                'GET',
                `/pet/report/petDetailedReport?petType=${selectedPetType}&licenseStatus=${licenseStatusFilter}&Page=${currentPage + 1}&Size=${itemsPerPage}`,
            );
            if (response && response.status === "success" && response.data) {
                setPets(response.data.petData || []);
                setTotalRecords(response.data.totalRecords || 0);
            } else {
                console.error('Error fetching data');
                setPets([]);
            }
        } catch (error) {
            console.error('API request failed', error);
            setPets([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    // Handle filter changes
    const handlePetTypeChange = (event) => {
        setSelectedPetType(event.target.value);
        setCurrentPage(0); // Reset to first page when filter changes
    };

    const handleLicenseStatusChange = (event) => {
        setLicenseStatusFilter(event.target.value);
        setCurrentPage(0); // Reset to first page when filter changes
    };

    // Fetch data when component mounts or filters/pagination change
    useEffect(() => {
        fetchPets();
    }, [currentPage, itemsPerPage, selectedPetType, licenseStatusFilter]);

    // Handle sending reminder
    const handleSendReminder = () => {
        // eslint-disable-next-line no-console
        console.log('Send Reminder - Under development');
    };

    const handleDeactivate = () => {
        // eslint-disable-next-line no-console
        console.log('Deactivate - Under development');
    };

    // Get status chip color
    const getStatusChipColor = (status: PetStatus | LicenseStatus) => {
        switch (status) {
            case PetStatus.ACTIVE:
            case LicenseStatus.ACTIVE:
                return 'success';
            case PetStatus.INACTIVE:
            case LicenseStatus.EXPIRED:
                return 'error';
            case LicenseStatus.EXPIRING_SOON:
                return 'warning';
            default:
                return 'default';
        }
    }

    // Calculate days left until expiration
    const getDaysLeft = (expiryDate) => {
        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    return (
        <Box>
            <Typography variant="h3" mx={3} sx={{ color: 'primary.main' }}>
                Pet License Details
            </Typography>
            <Container maxWidth="xl">
                <Paper sx={{ p: 2, mb: 3, mt: 1 }}>
                    <Grid container spacing={2} py={2}>
                        <Grid item xs={12} sm={2}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="pet-type-label">Pet Type</InputLabel>
                                <Select
                                    labelId="pet-type-label"
                                    value={selectedPetType}
                                    onChange={handlePetTypeChange}
                                    label="Pet Type"
                                >
                                    {PET_TYPES?.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>License Status</InputLabel>
                                <Select
                                    value={licenseStatusFilter}
                                    onChange={handleLicenseStatusChange}
                                    label="License Status"
                                >
                                   {LICENSE_STATUSES?.map((status) => (
                                        <MenuItem key={status} value={status}>{status}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    {columns.map((column) => (
                                        <TableCell key={column.field} sx={{
                                            textTransform: 'none',
                                        }}>{column.headerName}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">Loading...</TableCell>
                                    </TableRow>
                                ) : pets?.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    pets?.map(pet => (
                                        <TableRow key={pet.ApplicationID} >
                                            <TableCell>{pet.PetName}</TableCell>
                                            <TableCell>{pet.PetType}</TableCell>
                                            <TableCell>{pet.owner.FirstName} {pet.owner.LastName}</TableCell>
                                            <TableCell>{pet.owner.PhoneNumber}</TableCell>
                                            <TableCell>{new Date(pet.PermitExpirationDate).toLocaleDateString('en-US')}
                                                {pet.LicenseStatus === 'Expiring Soon' && (
                                                    <Typography
                                                        variant="caption"
                                                        fontSize={'small'}
                                                        color="body2"
                                                        sx={{ mx: 1, fontWeight: 'bold', backgroundColor: '#ffd1004a', padding: '0px 5px', borderRadius: '4px' }}
                                                        display="inline-block"
                                                    >
                                                        {getDaysLeft(pet.PermitExpirationDate)} days left
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell> {pet.petStatus || 'Active'} </TableCell>
                                            <TableCell>
                                                <Chip
                                                    variant='outlined'
                                                    label={pet.LicenseStatus}
                                                    color={getStatusChipColor(pet.LicenseStatus)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {(pet?.petStatus || 'Active') === 'Active' && (
                                                    <Box sx={{ display: 'inline-flex', width: { sm: 350, xs: 150, md: 250 }, flexDirection: { xs: 'column', sm: 'row', md: 'row' }, gap: 1, }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            sx={{ mr: 1 }}
                                                            size="small"
                                                            disabled={(pet?.petStatus || 'Active') === 'Active' && pet.LicenseStatus === 'Active'}
                                                            onClick={() => handleSendReminder()}
                                                        >
                                                            Send Reminder
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="inherit"
                                                            size="small"
                                                            onClick={() => handleDeactivate()}
                                                        >
                                                            Deactivate
                                                        </Button>
                                                    </Box>
                                                )}
                                            </TableCell>

                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalRecords}
                        page={currentPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={itemsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </Paper>
            </Container>
        </Box>
    );
}
export default PetLicenseDetails;